import { useEffect, useState } from "react";
import { Card } from "@/ui/atoms/Card";
import { Loader, Modal, Button, DialogFooter } from "@/ui/atoms";
import { Megaphone, Plus, Edit2, Trash2, Info, AlertTriangle, Check, X } from "lucide-react";
import type { TAsyncState } from "@/store";
import type { TApiAnnouncementType, TApiAnnouncementTargeting } from "@/apis/proda_finance/types";
import { EMPTY_ARRAY } from "@/utils/utils";

export type TAnnouncement = {
    id: string;
    title: string;
    message: string;
    type: TApiAnnouncementType;
    targeting: TApiAnnouncementTargeting;
    startAt: string;
    endAt: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
};

export type TCompany = {
    id: string;
    name: string;
    code: string;
};

export type TViewAnnouncementsParams = {
    useCases: () => {
        onMount: () => void;
        onUnmount: () => void;
        useAnnouncementsState: () => TAsyncState<TAnnouncement[]>;
        useAvailableCompanies: () => TCompany[];
        useIsProdaAdmin: () => boolean;
        onCreateAnnouncement: (data: {
            title: string;
            message: string;
            type: TApiAnnouncementType;
            targeting: TApiAnnouncementTargeting;
            startAt: string;
            endAt: string;
            isActive: boolean;
        }) => Promise<void>;
        onUpdateAnnouncement: (
            id: string,
            data: {
                title?: string;
                message?: string;
                type?: TApiAnnouncementType;
                targeting?: TApiAnnouncementTargeting;
                startAt?: string;
                endAt?: string;
                isActive?: boolean;
            },
        ) => Promise<void>;
        onDeleteAnnouncement: (id: string) => Promise<void>;
    };
};

export type TViewAnnouncementsProps = Record<string, never>;

// Role options (matching backend UserRole enum)
const roleOptions = [
    { value: "admin", label: "Admin" },
    { value: "account_manager", label: "Account Manager" },
    { value: "accountant", label: "Accountant" },
    { value: "business_partner", label: "Business Partner" },
    { value: "senior_management", label: "Senior Management" },
];

const announcementTypeOptions: { value: TApiAnnouncementType; label: string; icon: typeof Info }[] =
    [
        { value: "info", label: "Info", icon: Info },
        { value: "warning", label: "Warning", icon: AlertTriangle },
    ];

export const createViewAnnouncements = ({ useCases }: TViewAnnouncementsParams) => {
    return function ViewAnnouncements() {
        const {
            onMount,
            onUnmount,
            useAnnouncementsState,
            useAvailableCompanies,
            useIsProdaAdmin,
            onCreateAnnouncement,
            onUpdateAnnouncement,
            onDeleteAnnouncement,
        } = useCases();

        const announcementsState = useAnnouncementsState();
        const availableCompanies = useAvailableCompanies();
        const isProdaAdmin = useIsProdaAdmin();

        const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
        const [isEditModalOpen, setIsEditModalOpen] = useState(false);
        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
        const [deletingAnnouncement, setDeletingAnnouncement] = useState<TAnnouncement | null>(
            null,
        );
        const [isDeleting, setIsDeleting] = useState(false);
        const [editingAnnouncement, setEditingAnnouncement] = useState<TAnnouncement | null>(null);
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [error, setError] = useState<string | null>(null);

        // For non-Proda admins, default to their own company pre-selected
        const defaultAllCompanies = isProdaAdmin;
        const defaultCompanyIds =
            !isProdaAdmin && availableCompanies.length > 0 ? [availableCompanies[0]!.id] : [];
        const defaultCompanyRoles =
            !isProdaAdmin && availableCompanies.length > 0
                ? { [availableCompanies[0]!.id]: null as string[] | null }
                : {};

        // Create form state
        const [createFormData, setCreateFormData] = useState({
            title: "",
            message: "",
            type: "info" as TApiAnnouncementType,
            allCompanies: defaultAllCompanies,
            selectedCompanyIds: defaultCompanyIds,
            companyRoles: defaultCompanyRoles as Record<string, string[] | null>,
            startAt: "",
            endAt: "",
            isActive: true,
        });

        // Edit form state
        const [editFormData, setEditFormData] = useState({
            title: "",
            message: "",
            type: "info" as TApiAnnouncementType,
            allCompanies: defaultAllCompanies,
            selectedCompanyIds: defaultCompanyIds,
            companyRoles: defaultCompanyRoles as Record<string, string[] | null>,
            startAt: "",
            endAt: "",
            isActive: true,
        });

        useEffect(() => {
            onMount();
            return onUnmount;
        }, [onMount, onUnmount]);

        const resetCreateForm = () => {
            setCreateFormData({
                title: "",
                message: "",
                type: "info",
                allCompanies: defaultAllCompanies,
                selectedCompanyIds: defaultCompanyIds,
                companyRoles: defaultCompanyRoles as Record<string, string[] | null>,
                startAt: "",
                endAt: "",
                isActive: true,
            });
            setError(null);
            setIsCreateModalOpen(false);
        };

        const resetEditForm = () => {
            setEditFormData({
                title: "",
                message: "",
                type: "info",
                allCompanies: defaultAllCompanies,
                selectedCompanyIds: defaultCompanyIds,
                companyRoles: defaultCompanyRoles as Record<string, string[] | null>,
                startAt: "",
                endAt: "",
                isActive: true,
            });
            setEditingAnnouncement(null);
            setError(null);
            setIsEditModalOpen(false);
        };

        const openCreateModal = () => {
            resetCreateForm();
            setIsCreateModalOpen(true);
        };

        const openEditModal = (announcement: TAnnouncement) => {
            const targeting = announcement.targeting;
            setEditFormData({
                title: announcement.title,
                message: announcement.message,
                type: announcement.type,
                allCompanies: targeting.all_companies,
                selectedCompanyIds: targeting.company_ids || [],
                companyRoles: targeting.company_roles || {},
                startAt: new Date(announcement.startAt).toISOString().slice(0, 16),
                endAt: new Date(announcement.endAt).toISOString().slice(0, 16),
                isActive: announcement.isActive,
            });
            setEditingAnnouncement(announcement);
            setError(null);
            setIsEditModalOpen(true);
        };

        const handleCreateSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            setError(null);

            // Validation
            if (!createFormData.title.trim()) {
                setError("Title is required");
                return;
            }
            if (!createFormData.message.trim()) {
                setError("Message is required");
                return;
            }
            if (createFormData.message.length > 2000) {
                setError("Message must be 2000 characters or less");
                return;
            }
            if (!createFormData.startAt || !createFormData.endAt) {
                setError("Start date and end date are required");
                return;
            }
            if (new Date(createFormData.startAt) >= new Date(createFormData.endAt)) {
                setError("Start date must be before end date");
                return;
            }
            if (!createFormData.allCompanies && createFormData.selectedCompanyIds.length === 0) {
                setError("Please select at least one company or select 'All companies'");
                return;
            }

            setIsSubmitting(true);
            try {
                const targeting: TApiAnnouncementTargeting = {
                    all_companies: createFormData.allCompanies,
                    company_ids: createFormData.allCompanies
                        ? []
                        : createFormData.selectedCompanyIds,
                    company_roles: createFormData.allCompanies ? {} : createFormData.companyRoles,
                };

                await onCreateAnnouncement({
                    title: createFormData.title.trim(),
                    message: createFormData.message.trim(),
                    type: createFormData.type,
                    targeting,
                    startAt: new Date(createFormData.startAt).toISOString(),
                    endAt: new Date(createFormData.endAt).toISOString(),
                    isActive: createFormData.isActive,
                });
                resetCreateForm();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to create announcement");
            } finally {
                setIsSubmitting(false);
            }
        };

        const handleEditSubmit = async (e: React.FormEvent) => {
            e.preventDefault();
            if (!editingAnnouncement) return;

            setError(null);

            // Validation
            if (!editFormData.title.trim()) {
                setError("Title is required");
                return;
            }
            if (!editFormData.message.trim()) {
                setError("Message is required");
                return;
            }
            if (editFormData.message.length > 2000) {
                setError("Message must be 2000 characters or less");
                return;
            }
            if (!editFormData.startAt || !editFormData.endAt) {
                setError("Start date and end date are required");
                return;
            }
            if (new Date(editFormData.startAt) >= new Date(editFormData.endAt)) {
                setError("Start date must be before end date");
                return;
            }
            if (!editFormData.allCompanies && editFormData.selectedCompanyIds.length === 0) {
                setError("Please select at least one company or select 'All companies'");
                return;
            }

            setIsSubmitting(true);
            try {
                const targeting: TApiAnnouncementTargeting = {
                    all_companies: editFormData.allCompanies,
                    company_ids: editFormData.allCompanies ? [] : editFormData.selectedCompanyIds,
                    company_roles: editFormData.allCompanies ? {} : editFormData.companyRoles,
                };

                await onUpdateAnnouncement(editingAnnouncement.id, {
                    title: editFormData.title.trim(),
                    message: editFormData.message.trim(),
                    type: editFormData.type,
                    targeting,
                    startAt: new Date(editFormData.startAt).toISOString(),
                    endAt: new Date(editFormData.endAt).toISOString(),
                    isActive: editFormData.isActive,
                });
                resetEditForm();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to update announcement");
            } finally {
                setIsSubmitting(false);
            }
        };

        const openDeleteModal = (announcement: TAnnouncement) => {
            setDeletingAnnouncement(announcement);
            setError(null);
            setIsDeleteModalOpen(true);
        };

        const closeDeleteModal = () => {
            setDeletingAnnouncement(null);
            setError(null);
            setIsDeleteModalOpen(false);
        };

        const handleDelete = async () => {
            if (!deletingAnnouncement) return;

            setIsDeleting(true);
            setError(null);
            try {
                await onDeleteAnnouncement(deletingAnnouncement.id);
                closeDeleteModal();
            } catch (err) {
                setError(err instanceof Error ? err.message : "Failed to delete announcement");
            } finally {
                setIsDeleting(false);
            }
        };

        const formatDate = (dateString: string) => {
            return new Date(dateString).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
                hour: "2-digit",
                minute: "2-digit",
            });
        };

        const formatDateShort = (dateString: string) => {
            return new Date(dateString).toLocaleDateString("en-GB", {
                day: "2-digit",
                month: "short",
                year: "numeric",
            });
        };

        const getTargetingSummary = (targeting: TApiAnnouncementTargeting): string => {
            if (targeting.all_companies) {
                return "All companies";
            }
            if (targeting.company_ids.length === 0) {
                return "No companies";
            }
            const companyNames = targeting.company_ids
                .map((cid) => availableCompanies.find((c) => c.id === cid)?.name || cid)
                .join(", ");
            return `${targeting.company_ids.length} company(s): ${companyNames}`;
        };

        // Handle "All companies" checkbox
        const handleAllCompaniesToggle = (
            formData: typeof createFormData,
            setFormData: typeof setCreateFormData,
        ) => {
            const newAllCompanies = !formData.allCompanies;
            setFormData({
                ...formData,
                allCompanies: newAllCompanies,
                selectedCompanyIds: newAllCompanies ? [] : formData.selectedCompanyIds,
                companyRoles: newAllCompanies ? {} : formData.companyRoles,
            });
        };

        // Handle company selection
        const handleCompanyToggle = (
            companyId: string,
            formData: typeof createFormData,
            setFormData: typeof setCreateFormData,
        ) => {
            const isSelected = formData.selectedCompanyIds.includes(companyId);
            const newCompanyRoles = { ...formData.companyRoles };
            if (isSelected) {
                delete newCompanyRoles[companyId];
            } else {
                newCompanyRoles[companyId] = null; // null = all roles
            }
            setFormData({
                ...formData,
                allCompanies: false,
                selectedCompanyIds: isSelected
                    ? formData.selectedCompanyIds.filter((id) => id !== companyId)
                    : [...formData.selectedCompanyIds, companyId],
                companyRoles: newCompanyRoles,
            });
        };

        // Handle "All roles" for a company
        const handleAllRolesToggle = (
            companyId: string,
            formData: typeof createFormData,
            setFormData: typeof setCreateFormData,
        ) => {
            const currentRoles = formData.companyRoles[companyId];
            const isAllRoles = currentRoles === null;
            setFormData({
                ...formData,
                companyRoles: {
                    ...formData.companyRoles,
                    [companyId]: isAllRoles ? [] : null, // null = all roles
                },
            });
        };

        // Handle role selection for a company
        const handleRoleToggle = (
            companyId: string,
            role: string,
            formData: typeof createFormData,
            setFormData: typeof setCreateFormData,
        ) => {
            const currentRoles = formData.companyRoles[companyId] || [];
            const isSelected = Array.isArray(currentRoles) && currentRoles.includes(role);
            setFormData({
                ...formData,
                companyRoles: {
                    ...formData.companyRoles,
                    [companyId]: isSelected
                        ? currentRoles.filter((r) => r !== role)
                        : [...(Array.isArray(currentRoles) ? currentRoles : []), role],
                },
            });
        };

        const renderTargetingSection = (
            formData: typeof createFormData,
            setFormData: typeof setCreateFormData,
        ) => (
            <div className="space-y-4">
                <div>
                    <label className="block text-sm font-medium mb-2">Target Companies</label>
                    <div className="space-y-2">
                        {isProdaAdmin && (
                            <div className="flex items-center gap-2">
                                <input
                                    type="checkbox"
                                    id="allCompanies"
                                    checked={formData.allCompanies}
                                    onChange={() => handleAllCompaniesToggle(formData, setFormData)}
                                    className="h-4 w-4 rounded border-gray-300"
                                />
                                <label htmlFor="allCompanies" className="text-sm font-medium">
                                    All companies
                                </label>
                            </div>
                        )}
                        {!formData.allCompanies && (
                            <div
                                className={
                                    isProdaAdmin ? "ml-6 space-y-2 border-l-2 pl-4" : "space-y-2"
                                }
                            >
                                {availableCompanies.map((company) => (
                                    <div key={company.id} className="space-y-2">
                                        <div className="flex items-center gap-2">
                                            <input
                                                type="checkbox"
                                                id={`company-${company.id}`}
                                                checked={formData.selectedCompanyIds.includes(
                                                    company.id,
                                                )}
                                                onChange={() =>
                                                    handleCompanyToggle(
                                                        company.id,
                                                        formData,
                                                        setFormData,
                                                    )
                                                }
                                                className="h-4 w-4 rounded border-gray-300"
                                                disabled={!isProdaAdmin}
                                            />
                                            <label
                                                htmlFor={`company-${company.id}`}
                                                className="text-sm"
                                            >
                                                {company.name}
                                            </label>
                                        </div>
                                        {formData.selectedCompanyIds.includes(company.id) && (
                                            <div className="ml-6 space-y-2">
                                                <div className="flex items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        id={`allRoles-${company.id}`}
                                                        checked={
                                                            formData.companyRoles[company.id] ===
                                                            null
                                                        }
                                                        onChange={() =>
                                                            handleAllRolesToggle(
                                                                company.id,
                                                                formData,
                                                                setFormData,
                                                            )
                                                        }
                                                        className="h-4 w-4 rounded border-gray-300"
                                                    />
                                                    <label
                                                        htmlFor={`allRoles-${company.id}`}
                                                        className="text-sm font-medium"
                                                    >
                                                        All roles
                                                    </label>
                                                </div>
                                                {formData.companyRoles[company.id] !== null && (
                                                    <div className="ml-6 space-y-1">
                                                        {roleOptions.map((role) => {
                                                            const roles =
                                                                formData.companyRoles[company.id];
                                                            const isSelected =
                                                                Array.isArray(roles) &&
                                                                roles.includes(role.value);
                                                            return (
                                                                <div
                                                                    key={role.value}
                                                                    className="flex items-center gap-2"
                                                                >
                                                                    <input
                                                                        type="checkbox"
                                                                        id={`role-${company.id}-${role.value}`}
                                                                        checked={isSelected}
                                                                        onChange={() =>
                                                                            handleRoleToggle(
                                                                                company.id,
                                                                                role.value,
                                                                                formData,
                                                                                setFormData,
                                                                            )
                                                                        }
                                                                        className="h-4 w-4 rounded border-gray-300"
                                                                    />
                                                                    <label
                                                                        htmlFor={`role-${company.id}-${role.value}`}
                                                                        className="text-sm"
                                                                    >
                                                                        {role.label}
                                                                    </label>
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        );

        const renderCreateForm = () => (
            <form onSubmit={handleCreateSubmit} className="space-y-4">
                {error && (
                    <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                        {error}
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium mb-1">
                        Title <span className="text-destructive">*</span>
                    </label>
                    <input
                        type="text"
                        value={createFormData.title}
                        onChange={(e) =>
                            setCreateFormData({ ...createFormData, title: e.target.value })
                        }
                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Announcement title"
                        required
                        maxLength={255}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">
                        Message <span className="text-destructive">*</span>
                    </label>
                    <textarea
                        value={createFormData.message}
                        onChange={(e) =>
                            setCreateFormData({ ...createFormData, message: e.target.value })
                        }
                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary min-h-[120px]"
                        placeholder="Announcement message (max 2000 characters)"
                        required
                        maxLength={2000}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                        {createFormData.message.length}/2000 characters
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">
                        Type <span className="text-destructive">*</span>
                    </label>
                    <div className="flex gap-2">
                        {announcementTypeOptions.map((option) => {
                            const Icon = option.icon;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() =>
                                        setCreateFormData({ ...createFormData, type: option.value })
                                    }
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                                        createFormData.type === option.value
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "bg-background hover:bg-muted border-border"
                                    }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {renderTargetingSection(createFormData, setCreateFormData)}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Start Date <span className="text-destructive">*</span>
                        </label>
                        <input
                            type="datetime-local"
                            value={createFormData.startAt}
                            onChange={(e) =>
                                setCreateFormData({ ...createFormData, startAt: e.target.value })
                            }
                            className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            End Date <span className="text-destructive">*</span>
                        </label>
                        <input
                            type="datetime-local"
                            value={createFormData.endAt}
                            onChange={(e) =>
                                setCreateFormData({ ...createFormData, endAt: e.target.value })
                            }
                            className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                            required
                        />
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="createIsActive"
                        checked={createFormData.isActive}
                        onChange={(e) =>
                            setCreateFormData({ ...createFormData, isActive: e.target.checked })
                        }
                        className="h-4 w-4 rounded border-gray-300"
                    />
                    <label htmlFor="createIsActive" className="text-sm font-medium">
                        Active (non-dismissible)
                    </label>
                </div>

                <DialogFooter className="gap-2 pt-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={resetCreateForm}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Loader size="sm" className="mr-2" />
                                Creating...
                            </>
                        ) : (
                            "Create Announcement"
                        )}
                    </Button>
                </DialogFooter>
            </form>
        );

        const renderEditForm = () => (
            <form onSubmit={handleEditSubmit} className="space-y-4">
                {error && (
                    <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                        {error}
                    </div>
                )}

                <div>
                    <label className="block text-sm font-medium mb-1">
                        Title <span className="text-destructive">*</span>
                    </label>
                    <input
                        type="text"
                        value={editFormData.title}
                        onChange={(e) =>
                            setEditFormData({ ...editFormData, title: e.target.value })
                        }
                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                        placeholder="Announcement title"
                        required
                        maxLength={255}
                    />
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">
                        Message <span className="text-destructive">*</span>
                    </label>
                    <textarea
                        value={editFormData.message}
                        onChange={(e) =>
                            setEditFormData({ ...editFormData, message: e.target.value })
                        }
                        className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary min-h-[120px]"
                        placeholder="Announcement message (max 2000 characters)"
                        required
                        maxLength={2000}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                        {editFormData.message.length}/2000 characters
                    </p>
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">
                        Type <span className="text-destructive">*</span>
                    </label>
                    <div className="flex gap-2">
                        {announcementTypeOptions.map((option) => {
                            const Icon = option.icon;
                            return (
                                <button
                                    key={option.value}
                                    type="button"
                                    onClick={() =>
                                        setEditFormData({ ...editFormData, type: option.value })
                                    }
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                                        editFormData.type === option.value
                                            ? "bg-primary text-primary-foreground border-primary"
                                            : "bg-background hover:bg-muted border-border"
                                    }`}
                                >
                                    <Icon className="h-4 w-4" />
                                    {option.label}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {renderTargetingSection(editFormData, setEditFormData)}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            Start Date <span className="text-destructive">*</span>
                        </label>
                        <input
                            type="datetime-local"
                            value={editFormData.startAt}
                            onChange={(e) =>
                                setEditFormData({ ...editFormData, startAt: e.target.value })
                            }
                            className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                            required
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">
                            End Date <span className="text-destructive">*</span>
                        </label>
                        <input
                            type="datetime-local"
                            value={editFormData.endAt}
                            onChange={(e) =>
                                setEditFormData({ ...editFormData, endAt: e.target.value })
                            }
                            className="w-full px-3 py-2 border rounded-lg bg-background focus:outline-none focus:ring-2 focus:ring-primary"
                            required
                        />
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <input
                        type="checkbox"
                        id="editIsActive"
                        checked={editFormData.isActive}
                        onChange={(e) =>
                            setEditFormData({ ...editFormData, isActive: e.target.checked })
                        }
                        className="h-4 w-4 rounded border-gray-300"
                    />
                    <label htmlFor="editIsActive" className="text-sm font-medium">
                        Active (non-dismissible)
                    </label>
                </div>

                <DialogFooter className="gap-2 pt-2">
                    <Button
                        type="button"
                        variant="outline"
                        onClick={resetEditForm}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </Button>
                    <Button type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <Loader size="sm" className="mr-2" />
                                Updating...
                            </>
                        ) : (
                            "Update Announcement"
                        )}
                    </Button>
                </DialogFooter>
            </form>
        );

        if (announcementsState.status === "pending") {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader size="lg" />
                </div>
            );
        }

        if (announcementsState.status === "rejected") {
            return (
                <div className="space-y-6">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Megaphone className="h-6 w-6 text-primary" />
                            <h1 className="text-2xl font-semibold">Announcements</h1>
                        </div>
                    </div>
                    <Card className="p-6">
                        <div className="text-center py-12">
                            <p className="text-destructive">
                                Failed to load announcements:{" "}
                                {announcementsState.error?.message || "Unknown error"}
                            </p>
                        </div>
                    </Card>
                </div>
            );
        }

        const announcements = announcementsState.data ?? EMPTY_ARRAY;

        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Megaphone className="h-6 w-6 text-primary" />
                        <h1 className="text-2xl font-semibold">Announcements</h1>
                    </div>
                    <Button onClick={openCreateModal}>
                        <Plus className="h-4 w-4 mr-2" />
                        Create Announcement
                    </Button>
                </div>

                <Card className="p-6">
                    {announcements.length === 0 ? (
                        <div className="text-center py-12">
                            <Megaphone className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                            <h2 className="text-lg font-medium mb-2">No announcements</h2>
                            <p className="text-muted-foreground mb-4">
                                Create your first announcement to display banners in the UI.
                            </p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="border-b">
                                        <th className="text-left py-3 px-4 font-medium">Title</th>
                                        <th className="text-left py-3 px-4 font-medium">Type</th>
                                        <th className="text-left py-3 px-4 font-medium">Active</th>
                                        <th className="text-left py-3 px-4 font-medium">
                                            Start Date
                                        </th>
                                        <th className="text-left py-3 px-4 font-medium">
                                            End Date
                                        </th>
                                        <th className="text-left py-3 px-4 font-medium">
                                            Targeting
                                        </th>
                                        <th className="text-left py-3 px-4 font-medium">Created</th>
                                        <th className="text-right py-3 px-4 font-medium">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {announcements.map((announcement) => {
                                        const TypeIcon =
                                            announcement.type === "info" ? Info : AlertTriangle;
                                        return (
                                            <tr
                                                key={announcement.id}
                                                className="border-b hover:bg-muted/50 even:bg-muted/40"
                                            >
                                                <td className="py-3 px-4">
                                                    <div className="font-medium">
                                                        {announcement.title}
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center gap-2">
                                                        <TypeIcon
                                                            className={`h-4 w-4 ${
                                                                announcement.type === "info"
                                                                    ? "text-blue-500"
                                                                    : "text-amber-500"
                                                            }`}
                                                        />
                                                        <span className="capitalize">
                                                            {announcement.type}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="py-3 px-4">
                                                    {announcement.isActive ? (
                                                        <span className="inline-flex items-center gap-1 text-green-600">
                                                            <Check className="h-4 w-4" />
                                                            Active
                                                        </span>
                                                    ) : (
                                                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                                                            <X className="h-4 w-4" />
                                                            Dismissible
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="py-3 px-4 text-sm">
                                                    {formatDateShort(announcement.startAt)}
                                                </td>
                                                <td className="py-3 px-4 text-sm">
                                                    {formatDateShort(announcement.endAt)}
                                                </td>
                                                <td className="py-3 px-4 text-sm text-muted-foreground max-w-xs truncate">
                                                    {getTargetingSummary(announcement.targeting)}
                                                </td>
                                                <td className="py-3 px-4 text-sm text-muted-foreground">
                                                    {formatDate(announcement.createdAt)}
                                                </td>
                                                <td className="py-3 px-4">
                                                    <div className="flex items-center justify-end gap-2">
                                                        <button
                                                            onClick={() =>
                                                                openEditModal(announcement)
                                                            }
                                                            className="p-1.5 hover:bg-muted rounded transition-colors"
                                                            title="Edit"
                                                        >
                                                            <Edit2 className="h-4 w-4" />
                                                        </button>
                                                        <button
                                                            onClick={() =>
                                                                openDeleteModal(announcement)
                                                            }
                                                            className="p-1.5 hover:bg-destructive/10 text-destructive rounded transition-colors"
                                                            title="Delete"
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card>

                <Modal
                    open={isCreateModalOpen}
                    onOpenChange={(open) => !open && resetCreateForm()}
                    title="Create Announcement"
                >
                    {renderCreateForm()}
                </Modal>

                <Modal
                    open={isEditModalOpen}
                    onOpenChange={(open) => !open && resetEditForm()}
                    title="Edit Announcement"
                >
                    {renderEditForm()}
                </Modal>

                <Modal
                    open={isDeleteModalOpen}
                    onOpenChange={(open) => !open && closeDeleteModal()}
                    title="Delete Announcement"
                >
                    <div className="space-y-4">
                        {error && (
                            <div className="rounded-lg bg-destructive/10 p-3 text-destructive text-sm">
                                {error}
                            </div>
                        )}

                        <p className="text-sm text-muted-foreground">
                            Are you sure you want to delete{" "}
                            <strong>&ldquo;{deletingAnnouncement?.title}&rdquo;</strong>? This
                            action cannot be undone.
                        </p>

                        <DialogFooter className="gap-2 pt-2">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={closeDeleteModal}
                                disabled={isDeleting}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="button"
                                variant="destructive"
                                onClick={handleDelete}
                                disabled={isDeleting}
                            >
                                {isDeleting ? (
                                    <>
                                        <Loader size="sm" className="mr-2" />
                                        Deleting...
                                    </>
                                ) : (
                                    "Delete Announcement"
                                )}
                            </Button>
                        </DialogFooter>
                    </div>
                </Modal>
            </div>
        );
    };
};
